import {
  Inject,
  Injectable,
  InjectionToken,
  ModuleWithProviders,
  NgModule,
} from '@angular/core';
import { BackendFetchReturnTypes } from '../backend.model';
import {
  CollectionHasTotalCount,
  LegacySearchCollections,
} from '../collections.model';

export interface TransformedSearchResult<
  K extends keyof LegacySearchCollections
> {
  readonly documents: ReadonlyArray<LegacySearchCollections[K]>;
  readonly totalCount: CollectionHasTotalCount[K] extends true
    ? number
    : undefined;
}

export type SearchTransformer<K extends keyof LegacySearchCollections> = (
  value: BackendFetchReturnTypes[K],
  authUid: string,
) => TransformedSearchResult<K>;

export type SearchTransformersMap = {
  [K in keyof LegacySearchCollections]?: SearchTransformer<K>;
};

export interface SearchFeature<K extends keyof LegacySearchCollections> {
  readonly featureName: K;
  readonly transformer: SearchTransformer<K>;
}

export type SearchFeatures = ReadonlyArray<
  SearchFeature<keyof LegacySearchCollections>
>;

export const SEARCH_FEATURES = new InjectionToken<SearchFeatures>(
  'Search Transformers',
);

@Injectable()
export class SearchManager {
  private transformers: SearchTransformersMap = {};

  transform<K extends keyof LegacySearchCollections>(
    key: K,
    result: BackendFetchReturnTypes[K],
    authUid: string,
  ): TransformedSearchResult<K> {
    // FIXME: is the type casting really needed?
    return (this.transformers[key] as SearchTransformer<K>)(result, authUid);
  }

  addFeature<K extends keyof LegacySearchCollections>({
    featureName,
    transformer,
  }: SearchFeature<K>) {
    this.transformers[featureName] = transformer as any; // FIXME: any type
  }
}

@NgModule({})
export class SearchRootModule {}

@NgModule({})
export class SearchFeatureModule {
  constructor(
    searchManager: SearchManager,
    @Inject(SEARCH_FEATURES) searchFeatures: SearchFeatures,
  ) {
    searchFeatures.map(f => searchManager.addFeature(f));
  }
}

@NgModule({})
export class SearchModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: SearchRootModule,
      providers: [SearchManager],
    };
  }

  static forFeature<K extends keyof LegacySearchCollections>(
    featureName: K,
    transformer: SearchTransformer<K>,
  ): ModuleWithProviders {
    return {
      ngModule: SearchFeatureModule,
      providers: [
        {
          provide: SEARCH_FEATURES,
          multi: true,
          useValue: {
            featureName,
            transformer,
          },
        },
      ],
    };
  }
}
